import { useState, useMemo, useEffect } from 'react';
import {
  MatchState,
  TeamState,
  Player,
  Rally,
  SetRecord,
  LostReason,
  LOST_REASONS,
  LOST_REASON_LABELS,
  LOST_REASON_REQUIRES_WINNER,
  ValidationError,
  MATCH_CONFIG,
  ActiveTimeout,
} from '@/types';
import SetWonBanner from '@/components/modals/SetWonBanner';
import ServerSelectionModal from '@/components/modals/ServerSelectionModal';
import FirstServingTeamSelection from '@/components/modals/FirstServingTeamSelection';
import PointWinnerModal from '@/components/modals/PointWinnerModal';
import TimeoutModal from '@/components/modals/TimeoutModal';
import { ScoreBar } from '@/components/ScoreBar';
import ErrorPopup from '@/components/modals/ErrorPopup';
import RallyHistoryModal from '@/components/modals/RallyHistoryModal';
import UndoConfirmPopup from '@/components/modals/UndoConfirmPopup';
import AppHeader from '@/components/AppHeader';
import { TeamLogo } from '@/components/TeamLogo';
import { getTournament } from '@/lib/supabase';
import { getSetScoreColor } from '@/lib/setScoreColor';

interface LiveScoringProps {
  matchState: MatchState;
  servingTeam: TeamState | null;
  currentServer: Player | null;
  eligibleServers: { eligible: Player[]; ineligible: { player: Player; remaining: number }[] };
  serverSelectionNeeded: boolean;
  onRecordRally: (
    winningTeamId: string,
    lostReason: LostReason | null,
    pointWinnerId: string | null
  ) => Promise<void>;
  onSelectNextServer: (player: Player) => Promise<void>;
  onUndo: () => Promise<void>;
  onUndoServerSelection: () => Promise<void>;
  canUndoServerSelection: () => { canUndo: boolean; servingTeamId: string | null };
  onStartNewSet: (firstServingTeamId: string, firstServerId: string) => Promise<void>;
  onRecordServeFoul: (server: Player) => Promise<void>;
  onFetchSetRallies: (setId: string) => Promise<Rally[]>;
  onUpdateBestCatcher: (playerIds: string[]) => Promise<void>;
  onSignOut: () => void;
  tournamentId?: string;
  onExitToTournament?: (tournamentId: string) => void;
  readOnly?: boolean;
  // Timeout props
  activeTimeout?: ActiveTimeout | null;
  timeoutCounts?: Map<string, number>;
  onStartTimeout?: (teamId: string, teamName: string) => Promise<void>;
  onEndTimeout?: () => Promise<void>;
}

export default function LiveScoring({
  matchState,
  servingTeam,
  currentServer,
  eligibleServers,
  serverSelectionNeeded,
  onRecordRally,
  onSelectNextServer,
  onUndo,
  onUndoServerSelection,
  canUndoServerSelection,
  onStartNewSet,
  onRecordServeFoul,
  onFetchSetRallies,
  onUpdateBestCatcher,
  onSignOut,
  tournamentId,
  onExitToTournament,
  readOnly = false,
  activeTimeout,
  timeoutCounts,
  onStartTimeout,
  onEndTimeout,
}: LiveScoringProps) {
  const [showLostReason, setShowLostReason] = useState(false);
  const [showPointWinner, setShowPointWinner] = useState(false);
  // Auto-open server selection if recovery landed mid-service-break (no server assigned yet)
  const [showServerSelection, setShowServerSelection] = useState(serverSelectionNeeded && !!servingTeam);
  // Serving team selection needed when a set exists but no serving team assigned
  const [showTeamSelection, setShowTeamSelection] = useState(serverSelectionNeeded && !servingTeam);

  // Re-open the modals if serverSelectionNeeded changes after mount (e.g. after
  // undo or recovery where the component was already mounted with stale props).
  useEffect(() => {
    if (serverSelectionNeeded && !!servingTeam && !showServerSelection) {
      setShowServerSelection(true);
    }
    if (serverSelectionNeeded && !servingTeam && !showTeamSelection) {
      setShowTeamSelection(true);
    }
  }, [serverSelectionNeeded, servingTeam]);
  const [selectedServingTeam, setSelectedServingTeam] = useState<TeamState | null>(null);
  const [pendingTeamWonId, setPendingTeamWonId] = useState<string | null>(null);
  const [pendingLostReason, setPendingLostReason] = useState<LostReason | null>(null);
  const [error, setError] = useState<ValidationError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [historyModal, setHistoryModal] = useState<{
    setNumber: number;
    setRecord: SetRecord;
    rallies: Rally[];
  } | null>(null);
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const [undoType, setUndoType] = useState<'rally' | 'server' | null>(null);
  const [tournamentName, setTournamentName] = useState<string | null>(null);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);

  // Auto-open timeout modal if there's an active timeout (e.g., recovered from persistence)
  useEffect(() => {
    if (activeTimeout && onStartTimeout && onEndTimeout) {
      setShowTimeoutModal(true);
    }
  }, [activeTimeout, onStartTimeout, onEndTimeout]);

  // Load tournament name if match belongs to a tournament
  useEffect(() => {
    const tid = matchState.match?.tournamentId;
    if (!tid) { setTournamentName(null); return; }
    getTournament(tid)
      .then(t => setTournamentName(t.name))
      .catch(() => setTournamentName(null));
  }, [matchState.match?.tournamentId]);

  // Check if set was just won
  const showSetWonBanner = matchState.status === 'set-won' && matchState.currentSet;

  // Derive rotation dots
  const rotationDots = useMemo(() => {
    if (!servingTeam) return [];
    const playersServed = new Set<string>();
    const dots: { filled: boolean; isCurrent: boolean }[] = [];

    // Go through serve history
    for (let i = 0; i < 8; i++) {
      const playerId = servingTeam.rotationState.serveHistory[i];
      if (playerId) {
        playersServed.add(playerId);
        const isCurrent = playerId === currentServer?.id;
        dots.push({ filled: true, isCurrent });
      } else {
        dots.push({ filled: false, isCurrent: false });
      }
    }

    return dots;
  }, [servingTeam, currentServer]);

  // Get the team that lost the rally
  const losingTeam = useMemo(() => {
    if (!pendingTeamWonId || !matchState.teamA || !matchState.teamB) return null;
    return pendingTeamWonId === matchState.teamA.id ? matchState.teamB : matchState.teamA;
  }, [pendingTeamWonId, matchState.teamA, matchState.teamB]);

  // Handle team button click
  const handleTeamWonClick = (teamId: string) => {
    if (isSubmitting) return;
    setPendingTeamWonId(teamId);
    setShowLostReason(true);
  };

  // Handle lost reason selection
  const handleLostReasonSelect = async (reason: LostReason) => {
    setShowLostReason(false);
    setPendingLostReason(reason);

    if (LOST_REASON_REQUIRES_WINNER[reason]) {
      setShowPointWinner(true);
    } else {
      // Direct commit
      await commitRally(pendingTeamWonId!, reason, null);
    }
  };

  // Handle point winner selection
  const handlePointWinnerSelect = async (playerId: string) => {
    setShowPointWinner(false);
    await commitRally(pendingTeamWonId!, pendingLostReason!, playerId);
  };

  // Commit the rally
  const commitRally = async (
    winningTeamId: string,
    lostReason: LostReason,
    pointWinnerId: string | null
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onRecordRally(winningTeamId, lostReason, pointWinnerId);

      // Check for service break after recording
      const currentServingTeamId = matchState.currentSet?.servingTeamId;
      if (currentServingTeamId !== winningTeamId) {
        // Service break - but only trigger if not set-won
        if (matchState.status !== 'set-won' && matchState.status !== 'match-won') {
          setShowServerSelection(true);
        }
      }

      // Reset pending state for next rally
      setPendingTeamWonId(null);
      setPendingLostReason(null);
    } catch (err) {
      setError({
        id: 'rally-error',
        title: 'Rally Record Failed',
        message: err instanceof Error ? err.message : 'Failed to record rally.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle server selection
  const handleServerSelect = async (player: Player) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onSelectNextServer(player);
      setShowServerSelection(false);
    } catch (err) {
      setError({
        id: 'server-error',
        title: 'ROTATION VIOLATION',
        message: err instanceof Error ? err.message : 'Invalid server selection.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Undo availability — server selection undo is only allowed when the most
  // recent action was a server selection (no subsequent rally recorded).
  const { canUndo: canUndoServer } = useMemo(
    () => canUndoServerSelection(),
    [canUndoServerSelection]
  );
  const canUndoRally = (matchState.currentSet?.rallies.length ?? 0) > 0;

  // Handle undo
  const handleUndoRallyClick = () => {
    setUndoType('rally');
    setShowUndoConfirm(true);
  };

  const handleUndoServerClick = () => {
    setUndoType('server');
    setShowUndoConfirm(true);
  };

  const handleUndoConfirm = async () => {
    setShowUndoConfirm(false);
    const type = undoType;
    setUndoType(null);

    if (type === 'server') {
      setIsSubmitting(true);
      try {
        await onUndoServerSelection();
        setPendingTeamWonId(null);
        setPendingLostReason(null);
        setShowServerSelection(true);
      } catch (err) {
        setError({
          id: 'undo-error',
          title: 'Undo Failed',
          message: 'Could not undo server selection.',
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(true);
      try {
        await onUndo();
      } catch (err) {
        setError({
          id: 'undo-error',
          title: 'Undo Failed',
          message: 'Could not undo the last rally.',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleUndoCancel = () => {
    setShowUndoConfirm(false);
    setUndoType(null);
  };

  // Handle start next set
  const handleStartNextSet = async (firstServingTeamId: string, firstServerId: string) => {
    setIsSubmitting(true);
    try {
      await onStartNewSet(firstServingTeamId, firstServerId);
    } catch (err) {
      setError({
        id: 'set-error',
        title: 'Failed to Start Set',
        message: 'Could not start the next set.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dismiss error popup
  const dismissError = () => {
    setError(null);
  };

  // Cancel lost reason selection
  const cancelLostReason = () => {
    setShowLostReason(false);
    setPendingTeamWonId(null);
    setPendingLostReason(null);
  };

  // Cancel point winner selection
  const cancelPointWinner = () => {
    setShowPointWinner(false);
    setPendingTeamWonId(null);
    setPendingLostReason(null);
  };

  // Cancel server selection (shouldn't happen, but UI provides back button)
  const cancelServerSelection = () => {
    setShowServerSelection(false);
  };

  // Handle serving team selection for new set
  const handleSelectServingTeam = (teamId: string) => {
    const team = teamId === matchState.teamA?.id ? matchState.teamA : matchState.teamB;
    if (team) {
      setSelectedServingTeam(team);
      setShowTeamSelection(false);
      setShowServerSelection(true);
    }
  };

  // Handle server selection after team selection
  const handleSelectFirstServer = async (player: Player) => {
    if (!selectedServingTeam) return;
    setIsSubmitting(true);
    try {
      await onStartNewSet(selectedServingTeam.id, player.id);
      setShowServerSelection(false);
      setSelectedServingTeam(null);
    } catch (err) {
      setError({
        id: 'server-error',
        title: 'ROTATION VIOLATION',
        message: err instanceof Error ? err.message : 'Invalid server selection.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open rally history for the current (in-progress) set
  const openCurrentSetHistory = () => {
    if (!matchState.currentSet || !matchState.teamA || !matchState.teamB) return;
    const setRecord = matchState.sets.find(s => s.endedAt === null);
    if (!setRecord) return;
    setHistoryModal({
      setNumber: matchState.currentSetNumber,
      setRecord,
      rallies: matchState.currentSet.rallies,
    });
  };

  // Open rally history for a completed set (fetches from DB)
  const openPastSetHistory = async (set: SetRecord) => {
    setHistoryLoading(set.id);
    try {
      const rallies = await onFetchSetRallies(set.id);
      setHistoryModal({ setNumber: set.setNumber, setRecord: set, rallies });
    } catch {
      // silently fail — no state corruption
    } finally {
      setHistoryLoading(null);
    }
  };

  // Handle serve foul — records rally + service break automatically, modal stays open for opponent
  const handleServeFoul = async (player: Player) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onRecordServeFoul(player);
      // showServerSelection stays true; after state updates servingTeam becomes the opponent,
      // so the modal re-renders to show the opponent's server selection automatically.
    } catch (err) {
      setError({
        id: 'serve-foul-error',
        title: 'Serve Foul Failed',
        message: err instanceof Error ? err.message : 'Failed to record serve foul.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!matchState.match || !matchState.teamA || !matchState.teamB || !matchState.currentSet) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  // Destructure for type narrowing
  const { teamA, teamB, currentSet, match, currentSetNumber } = matchState;

  // Completed sets (those with winners) - handle both camelCase and snake_case
  const completedSets = matchState.sets.filter(s => s.winnerId || (s as any).winner_id || s.endedAt || (s as any).ended_at);

  // Determine winning team for modals
  const winningTeam = pendingTeamWonId === teamA.id ? teamA : teamB;

  return (
    <div className="min-h-screen bg-background flex flex-col pb-safe">
      {/* App Header with tournament context */}
      <AppHeader
        onSignOut={onSignOut}
        title={tournamentName || `Match #${match.matchNumber}`}
        subtitle={tournamentName ? `${match.matchType.charAt(0).toUpperCase() + match.matchType.slice(1)} Match #${match.matchNumber}` : match.date}
        onNavigate={tournamentId && onExitToTournament ? () => setShowExitConfirm(true) : undefined}
        navIcon={tournamentId && onExitToTournament ? 'tournaments' : undefined}
        tournamentId={tournamentId}
        tournamentName={tournamentName ?? undefined}
      />

      {/* Match Meta Strip */}
      <div className="bg-surface border-b border-border px-4 py-2">
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>MATCH #{match.matchNumber}</span>
          <span>{match.date}</span>
          <span className="uppercase">{match.matchType}</span>
        </div>
        <p className="text-center text-text-secondary text-xs mt-1">
          Best of {MATCH_CONFIG[match.matchType].maxSets} Sets
        </p>
      </div>

      {/* Exit to Tournament Confirmation */}
      {showExitConfirm && tournamentId && onExitToTournament && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-base font-bold text-text-primary mb-1">Match in Progress</h3>
            <p className="text-sm text-text-secondary mb-5">
              What would you like to do?
            </p>
            <div className="space-y-2">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="w-full py-3 px-4 rounded-lg font-bold text-sm bg-success text-white transition-all active:scale-[0.98]"
              >
                CONTINUE SCORING
              </button>
              <button
                onClick={() => { setShowExitConfirm(false); onExitToTournament(tournamentId); }}
                className="w-full py-3 px-4 rounded-lg font-semibold text-sm bg-surface border border-border text-text-primary transition-all active:scale-[0.98] hover:bg-surface-raised"
              >
                SAVE AND EXIT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Serving-team-first score ordering */}
      {(() => {
        const isTeamAServing = matchState.currentSet?.servingTeamId === teamA.id;
        const firstScoreTeam = isTeamAServing ? teamA : teamB;
        const secondScoreTeam = isTeamAServing ? teamB : teamA;
        const firstScore = isTeamAServing ? currentSet.scoreA : currentSet.scoreB;
        const secondScore = isTeamAServing ? currentSet.scoreB : currentSet.scoreA;
        const firstDotsColor = isTeamAServing ? 'bg-team-a' : 'bg-team-b';
        const secondDotsColor = isTeamAServing ? 'bg-team-b' : 'bg-team-a';
        const firstTimeoutCount = timeoutCounts?.get(firstScoreTeam.id) ?? 0;
        const secondTimeoutCount = timeoutCounts?.get(secondScoreTeam.id) ?? 0;

        console.log("========== TIMEOUT LOOKUP ==========");
        console.log({
          firstTeamId: firstScoreTeam.id,
          firstTeamShortName: firstScoreTeam.shortName,
          firstTeamName: firstScoreTeam.name,
          secondTeamId: secondScoreTeam.id,
          secondTeamShortName: secondScoreTeam.shortName,
          secondTeamName: secondScoreTeam.name,
          timeoutMap: timeoutCounts ? Object.fromEntries(timeoutCounts) : null,
          firstLookup: timeoutCounts?.get(firstScoreTeam.id),
          secondLookup: timeoutCounts?.get(secondScoreTeam.id),
        });
        if (timeoutCounts) {
          for (const [teamId, count] of timeoutCounts.entries()) {
            console.log("[Timeout Map Entry]", { teamId, count });
          }
        }
        if (firstTimeoutCount === undefined || secondTimeoutCount === undefined) {
          console.log({
            firstTeamObject: firstScoreTeam,
            secondTeamObject: secondScoreTeam,
          });
        }
        console.log("====================================");

        return (
          <>
            {/* Scoreboard — serving team score appears first */}
            <div className="bg-surface border-b border-border">
              <div className="grid grid-cols-2 divide-x divide-border">
                {/* Serving Team Score (left) - timeout badge at top-left corner */}
                <div className="py-4 px-2 sm:px-4 relative">
                  <span className="absolute top-1.5 left-2 sm:left-4 text-xs font-bold text-white">
                    T{firstTimeoutCount}
                  </span>
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <TeamLogo team={firstScoreTeam} size="sm" />
                    <span className="text-xs font-semibold uppercase" style={{ color: firstScoreTeam.color }}>{firstScoreTeam.shortName}</span>
                  </div>
                  <p className="font-mono-nums text-6xl sm:text-7xl font-bold text-center text-text-primary">
                    {String(firstScore).padStart(2, '0')}
                  </p>
                  <div className="flex justify-center gap-1 mt-3">
                    {Array.from({ length: MATCH_CONFIG[match.matchType].maxSets }).map((_, i) => (
                      <span
                        key={i}
                        className={`w-3 h-3 rounded-full ${
                          i < firstScoreTeam.setsWon ? firstDotsColor : 'bg-border'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Receiving Team Score (right) - timeout badge at top-right corner */}
                <div className="py-4 px-2 sm:px-4 relative">
                  <span className="absolute top-1.5 right-2 sm:right-4 text-xs font-bold text-white">
                    T{secondTimeoutCount}
                  </span>
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <span className="text-xs font-semibold uppercase" style={{ color: secondScoreTeam.color }}>{secondScoreTeam.shortName}</span>
                    <TeamLogo team={secondScoreTeam} size="sm" />
                  </div>
                  <p className="font-mono-nums text-6xl sm:text-7xl font-bold text-center text-text-primary">
                    {String(secondScore).padStart(2, '0')}
                  </p>
                  <div className="flex justify-center gap-1 mt-3">
                    {Array.from({ length: MATCH_CONFIG[match.matchType].maxSets }).map((_, i) => (
                      <span
                        key={i}
                        className={`w-3 h-3 rounded-full ${
                          i < secondScoreTeam.setsWon ? secondDotsColor : 'bg-border'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Current Set & Completed Sets Display */}
      <div className="bg-surface-raised border-b border-border px-4 py-3">
        {/* Current Set Header */}
        <div className="text-center mb-2">
          <span className="text-sm font-bold text-text-primary">
            SET {currentSetNumber} OF {MATCH_CONFIG[match.matchType].maxSets}
          </span>
          {currentSet.goldenPoint && (
            <span className="ml-2 px-2 py-0.5 bg-warning text-black text-xs font-bold rounded">
              GOLDEN POINT
            </span>
          )}
        </div>

        {/* Completed Sets */}
        {completedSets && completedSets.length > 0 && (
          <div className="space-y-1">
            {completedSets.map((set) => {
              const scoreA = set.scoreA ?? 0;
              const scoreB = set.scoreB ?? 0;
              const setNumber = set.setNumber;
              const { color: scoreColor } = getSetScoreColor(set, teamA, teamB);

              return (
                <div
                  key={set.id}
                  className="flex items-center justify-center gap-3 text-xs"
                >
                  <span className="text-text-muted w-12">Set {setNumber}</span>
                  <span className="font-mono-nums font-bold" style={{ color: scoreColor ?? undefined }}>
                    {teamA.shortName} {scoreA}
                  </span>
                  <span className="text-text-muted">-</span>
                  <span className="font-mono-nums font-bold" style={{ color: scoreColor ?? undefined }}>
                    {scoreB} {teamB.shortName}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Sets Won Summary */}
        <div className="flex justify-center gap-4 mt-2 pt-2 border-t border-border">
          <div className="text-center">
            <span className="text-xs text-text-muted block">Sets Won</span>
            <span className="font-bold" style={{ color: teamA.color }}>{teamA.setsWon}</span>
          </div>
          <span className="text-text-muted">|</span>
          <div className="text-center">
            <span className="text-xs text-text-muted block">Sets Won</span>
            <span className="font-bold" style={{ color: teamB.color }}>{teamB.setsWon}</span>
          </div>
        </div>
      </div>

      {/* Serving Panel */}
      {servingTeam && currentServer && !showSetWonBanner && (
        <div
          className={`mx-4 mt-3 rounded-lg p-4 border transition-all ${
            servingTeam.id === teamA.id ? 'glow-team-a border-team-a' : 'glow-team-b border-team-b'
          }`}
          style={{ backgroundColor: `${servingTeam.color}10` }}
        >
          <div className="flex items-start justify-between gap-3">
            {/* Left Side: Serving Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full bg-serving pulse-serve" />
                <span className="text-xs font-semibold uppercase text-serving">Serving Now</span>
              </div>
              <p
                className="text-lg font-bold truncate"
                style={{ color: servingTeam.color }}
              >
                {servingTeam.name.toUpperCase()}
              </p>
              <p
                className="text-2xl font-bold truncate"
                style={{ color: servingTeam.color }}
              >
                {currentServer.name}
              </p>
              <div className="flex items-center gap-1.5 mt-3">
                <span className="text-xs text-text-muted mr-1">ROTATION</span>
                {rotationDots.slice(0, 8).map((dot, i) => (
                  <span
                    key={i}
                    className={`w-3 h-3 rounded-full border ${
                      dot.filled
                        ? dot.isCurrent
                          ? 'bg-serving border-serving'
                          : 'bg-text-muted border-text-muted'
                        : 'bg-transparent border-border'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Right Side: Team Logo */}
            <div className="flex-shrink-0 self-center sm:self-start">
              <TeamLogo team={servingTeam} size="xl" className="sm:w-20 sm:h-20" />
            </div>
          </div>
        </div>
      )}

      {/* Content Area with Rally Buttons and Recent Rallies */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Rally Buttons */}
        {!readOnly && !showSetWonBanner && (
          <div className="space-y-3 mb-6">
            <button
              onClick={() => handleTeamWonClick(teamA.id)}
              disabled={isSubmitting}
              className="btn-primary text-white font-bold py-5"
              style={{ backgroundColor: teamA.color }}
            >
              <span className="block text-sm opacity-80">{teamA.shortName}</span>
              <span className="text-lg">WON THIS RALLY</span>
            </button>
            <button
              onClick={() => handleTeamWonClick(teamB.id)}
              disabled={isSubmitting}
              className="btn-primary text-white font-bold py-5"
              style={{ backgroundColor: teamB.color }}
            >
              <span className="block text-sm opacity-80">{teamB.shortName}</span>
              <span className="text-lg">WON THIS RALLY</span>
            </button>
          </div>
        )}

        {/* Recent Rallies */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase text-text-muted">Recent Rallies</h3>
            {currentSet.rallies.length > 0 && (
              <button
                onClick={openCurrentSetHistory}
                className="text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                View All ({currentSet.rallies.length})
              </button>
            )}
          </div>
          <div className="bg-surface rounded-lg border border-border overflow-hidden">
            {currentSet.rallies.length === 0 ? (
              <p className="text-text-muted text-sm p-3 text-center">No rallies recorded yet</p>
            ) : (() => {
              // rallies are descending — compute running scores ascending then display descending
              const allPlayers = [...teamA.players, ...teamB.players];
              const ascending = [...currentSet.rallies].reverse();
              const runningScores: { a: number; b: number }[] = [];
              let a = 0, b = 0;
              for (const r of ascending) {
                if (r.pointWonById === teamA.id) a++;
                else if (r.pointWonById === teamB.id) b++;
                runningScores.push({ a, b });
              }
              // show last 5, descending
              return currentSet.rallies.slice(0, 5).map((rally, index) => {
                const rallyServingTeam = rally.servingTeamId === teamA.id ? teamA : teamB;
                const rallyWinningTeam = rally.pointWonById === teamA.id ? teamA : teamB;
                const server = allPlayers.find(p => p.id === rally.serverId);
                const pointWinner = allPlayers.find(p => p.id === rally.pointWinnerId);
                // index 0 = most recent rally = last entry in runningScores
                const scoreIdx = runningScores.length - 1 - index;
                const score = runningScores[scoreIdx];

                return (
                  <div
                    key={rally.id}
                    className={`flex items-center gap-2 px-3 py-2 text-xs ${
                      index !== 0 ? 'border-t border-border' : ''
                    }`}
                  >
                    <span className="text-text-muted w-5 shrink-0">#{rally.rallyNumber}</span>
                    <span className="font-semibold w-8 shrink-0" style={{ color: rallyServingTeam.color }}>
                      {rallyServingTeam.shortName}
                    </span>
                    <span className="flex-1 truncate" style={{ color: rallyServingTeam.color }}>{server?.name}</span>
                    <span className="text-text-muted shrink-0">→</span>
                    <span className="font-semibold w-8 shrink-0" style={{ color: rallyWinningTeam.color }}>
                      {rallyWinningTeam.shortName}
                    </span>
                    <span className="text-text-muted w-10 shrink-0">{rally.lostReason}</span>
                    <span className="w-16 truncate shrink-0" style={{ color: rallyWinningTeam.color }}>{pointWinner?.name ?? ''}</span>
                    {score && (
                      <span className="font-mono font-bold text-[11px] shrink-0 ml-auto pl-2">
                        <span style={{ color: teamA.color }}>{score.a}</span>
                        <span className="text-text-muted">–</span>
                        <span style={{ color: teamB.color }}>{score.b}</span>
                      </span>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Previous Set History */}
        {matchState.sets.filter(s => s.endedAt !== null).length > 0 && (
          <div className="mt-4">
            <h3 className="text-xs font-semibold uppercase text-text-muted mb-2">Set History</h3>
            <div className="space-y-2">
              {matchState.sets
                .filter(s => s.endedAt !== null)
                .sort((a, b) => a.setNumber - b.setNumber)
                .map(set => {
                  const winner = set.winnerId === teamA.id ? teamA : teamB;
                  const isLoading = historyLoading === set.id;
                  const { color: scoreColor } = getSetScoreColor(set, teamA, teamB);
                  return (
                    <button
                      key={set.id}
                      onClick={() => !isLoading && openPastSetHistory(set)}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-surface border border-border rounded-lg hover:border-text-muted transition-colors text-xs disabled:opacity-60"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-text-muted font-semibold">Set {set.setNumber}</span>
                        <span className="font-mono-nums font-bold" style={{ color: scoreColor ?? undefined }}>
                          {set.scoreA} – {set.scoreB}
                        </span>
                        <span
                          className="font-bold text-[10px] px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: `${winner.color}20`, color: winner.color }}
                        >
                          {winner.shortName}
                        </span>
                      </div>
                      {isLoading ? (
                        <span className="w-4 h-4 border-2 border-border border-t-text-secondary rounded-full animate-spin" />
                      ) : (
                        <span className="text-text-muted">›</span>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {!readOnly && !showSetWonBanner && (
        <div className="bg-surface border-t border-border px-4 py-3 flex items-center justify-between safe-area-bottom">
          <div className="flex items-center gap-3">
            <button
              onClick={handleUndoRallyClick}
              disabled={isSubmitting || !canUndoRally}
              className="text-sm text-error font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Undo Last Rally
            </button>
            <button
              onClick={handleUndoServerClick}
              disabled={isSubmitting || !canUndoServer}
              className="text-sm text-warning font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              title={
                !canUndoServer
                  ? 'Only available immediately after a server selection'
                  : 'Reopen server selection'
              }
            >
              Undo Server Selection
            </button>
          </div>
          <button
            onClick={() => setShowTimeoutModal(true)}
            disabled={isSubmitting}
            className="text-sm text-warning font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            TIMEOUT
          </button>
        </div>
      )}

      {/* Lost Reason Bottom Sheet */}
      {showLostReason && losingTeam && (
        <div className="fixed inset-0 bg-black/80 z-40">
          <div className="absolute bottom-0 left-0 right-0 bg-surface border-t border-border rounded-t-2xl p-4 pb-safe animate-slide-up">
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
            <ScoreBar teamA={teamA} teamB={teamB} scoreA={currentSet.scoreA} scoreB={currentSet.scoreB} servingTeamId={matchState.currentSet?.servingTeamId} />
            <h2 className="text-lg font-bold text-text-primary text-center mb-1">
              HOW DID {winningTeam?.name.toUpperCase()} WIN?
            </h2>
            <p className="text-text-secondary text-sm text-center mb-4">
              Select the reason for the rally outcome
            </p>

            <div className="grid grid-cols-2 gap-3 mb-4">
              {LOST_REASONS.map((reason) => {
                const requiresWinner = LOST_REASON_REQUIRES_WINNER[reason];
                // ACE is only valid when the serving team wins the rally
                const isAceDisabled = reason === 'ACE' && pendingTeamWonId !== matchState.currentSet?.servingTeamId;
                return (
                  <button
                    key={reason}
                    onClick={() => !isAceDisabled && handleLostReasonSelect(reason)}
                    disabled={isAceDisabled}
                    className={`py-4 px-3 rounded-lg font-bold text-lg transition-all ${
                      isAceDisabled
                        ? 'bg-surface border border-border text-text-muted opacity-40 cursor-not-allowed'
                        : requiresWinner
                          ? 'bg-surface-raised border-2 border-serving text-serving'
                          : 'bg-surface border border-border text-text-primary'
                    }`}
                  >
                    <span className="block">{LOST_REASON_LABELS[reason]}</span>
                    <span className="text-xs font-normal opacity-60">
                      {isAceDisabled ? 'server only' : requiresWinner ? 'winner ✓' : 'no winner'}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              onClick={cancelLostReason}
              className="btn-secondary text-text-secondary"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* Point Winner Selection */}
      {showPointWinner && pendingTeamWonId && winningTeam && (
        <PointWinnerModal
          team={winningTeam}
          lostReason={pendingLostReason!}
          onSelect={handlePointWinnerSelect}
          onCancel={cancelPointWinner}
          scoreTeamA={teamA}
          scoreTeamB={teamB}
          scoreA={currentSet.scoreA}
          scoreB={currentSet.scoreB}
          servingTeamId={matchState.currentSet?.servingTeamId}
        />
      )}

      {/* Server Selection Modal */}
      {showServerSelection && servingTeam && (
        <ServerSelectionModal
          team={servingTeam}
          eligibleServers={eligibleServers}
          onSelect={handleServerSelect}
          onCancel={cancelServerSelection}
          reason="service-break"
          onServeFoul={handleServeFoul}
          onTimeout={onStartTimeout ? () => setShowTimeoutModal(true) : undefined}
          scoreTeamA={teamA}
          scoreTeamB={teamB}
          scoreA={currentSet.scoreA}
          scoreB={currentSet.scoreB}
          servingTeamId={matchState.currentSet?.servingTeamId}
        />
      )}

      {/* Set Won Banner */}
      {showSetWonBanner && matchState.currentSet && (
        <SetWonBanner
          matchState={matchState}
          onStartNextSet={handleStartNextSet}
          onUpdateBestCatcher={onUpdateBestCatcher}
        />
      )}

      {/* First Serving Team Selection Modal (for recovery when no serving team assigned) */}
      {showTeamSelection && matchState.teamA && matchState.teamB && (
        <FirstServingTeamSelection
          teamA={matchState.teamA}
          teamB={matchState.teamB}
          setNumber={matchState.currentSetNumber}
          onSelect={handleSelectServingTeam}
        />
      )}

      {/* First Server Selection Modal (after team selection) */}
      {showServerSelection && selectedServingTeam && (
        <ServerSelectionModal
          team={selectedServingTeam}
          eligibleServers={{ eligible: selectedServingTeam.players, ineligible: [] }}
          onSelect={handleSelectFirstServer}
          onCancel={() => { setShowServerSelection(false); setSelectedServingTeam(null); }}
          reason="first-server"
        />
      )}

      {/* Error Popup */}
      {error && (
        <ErrorPopup
          title={error.title}
          message={error.message}
          onDismiss={dismissError}
        />
      )}

      {/* Undo Confirmation Popup */}
      {showUndoConfirm && (
        <UndoConfirmPopup
          onConfirm={handleUndoConfirm}
          onCancel={handleUndoCancel}
          title={undoType === 'server' ? 'Undo Server Selection?' : 'Undo Last Rally?'}
          message={
            undoType === 'server'
              ? 'This will clear the current server and reopen the server selection screen. The point and score will be kept.'
              : 'This will remove the last recorded rally and restore the match state to before it was recorded.'
          }
          confirmLabel={undoType === 'server' ? 'Reopen Selection' : 'Undo Last Rally'}
          variant={undoType === 'server' ? 'warning' : 'error'}
        />
      )}

      {/* Rally History Modal */}
      {historyModal && matchState.teamA && matchState.teamB && (
        <RallyHistoryModal
          setNumber={historyModal.setNumber}
          setRecord={historyModal.setRecord}
          rallies={historyModal.rallies}
          teamA={matchState.teamA}
          teamB={matchState.teamB}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {/* Timeout Modal */}
      {showTimeoutModal && matchState.teamA && matchState.teamB && (
        <TimeoutModal
          teamA={matchState.teamA}
          teamB={matchState.teamB}
          currentSetNumber={currentSetNumber}
          activeTimeout={activeTimeout}
          timeoutCounts={timeoutCounts}
          onStartTimeout={async (teamId, teamName) => {
            if (onStartTimeout) await onStartTimeout(teamId, teamName);
          }}
          onEndTimeout={async () => {
            if (onEndTimeout) await onEndTimeout();
          }}
          onClose={() => setShowTimeoutModal(false)}
        />
      )}
    </div>
  );
}
